import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, ChevronDown, CircleCheckBig, Clock, Trophy } from 'lucide-react';
import { courses, getCourse } from '../content';
import { useProgress } from '../hooks/useProgress';
import Blocks from '../components/Blocks';
import Icon from '../components/Icon';
import Confetti from '../components/Confetti';
import Quiz from '../components/Quiz';
import NotFound from './NotFound';

function Outline({ course, current }) {
  const { isDone, quizScore } = useProgress();
  const [open, setOpen] = useState(false);
  return (
    <aside className={`outline ${open ? 'is-open' : ''}`}>
      <button type="button" className="outline-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>Step {course.step} · {course.title}</span>
        <ChevronDown size={18} />
      </button>
      <div className="outline-body">
        <Link to={`/course/${course.id}`} className="outline-course">
          <span className="outline-course-icon"><Icon name={course.icon} size={18} /></span>
          <span><small>Step {course.step}</small>{course.title}</span>
        </Link>
        <ol>
          {course.lessons.map((l, i) => (
            <li key={l.id}>
              <Link to={`/course/${course.id}/${l.id}`} className={`${l.id === current ? 'is-current' : ''} ${isDone(course.id, l.id) ? 'is-done' : ''}`}>
                <span className="outline-num">{isDone(course.id, l.id) ? <Check size={13} /> : i + 1}</span>
                {l.title}
              </Link>
            </li>
          ))}
          <li>
            <Link to={`/course/${course.id}/quiz`} className={current === 'quiz' ? 'is-current' : ''}>
              <span className="outline-num"><Trophy size={13} /></span>
              Quiz {quizScore(course.id) !== undefined && `(${quizScore(course.id)}/${course.quiz.length})`}
            </Link>
          </li>
        </ol>
      </div>
    </aside>
  );
}

function ReadingBar() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 200, damping: 30 });
  return <motion.div className="reading-bar" style={{ scaleX }} />;
}

export default function LessonPage() {
  const { courseId, lessonId } = useParams();
  const course = getCourse(courseId);
  const { isDone, toggleDone } = useProgress();
  const [burst, setBurst] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [courseId, lessonId]);

  if (!course) return <NotFound />;
  const style = { '--c1': course.colors[0], '--c2': course.colors[1] };
  const nextCourse = courses[courses.indexOf(course) + 1];

  if (lessonId === 'quiz') {
    return (
      <div className="page lesson-layout" style={style}>
        <Outline course={course} current="quiz" />
        <article className="lesson">
          <header className="lesson-header">
            <span className="lesson-icon"><Trophy size={28} /></span>
            <div>
              <p className="eyebrow"><Link to={`/course/${course.id}`}>Step {course.step} · {course.title}</Link></p>
              <h1>Step {course.step} quiz</h1>
              <p className="lede">Answer each question to reveal the explanation. Your best score is saved.</p>
            </div>
          </header>
          <Quiz course={course} next={nextCourse} />
        </article>
      </div>
    );
  }

  const idx = course.lessons.findIndex((l) => l.id === lessonId);
  if (idx < 0) return <NotFound />;
  const lesson = course.lessons[idx];
  const prev = course.lessons[idx - 1];
  const next = course.lessons[idx + 1];
  const done = isDone(course.id, lesson.id);

  const complete = () => {
    if (!done) setBurst((b) => b + 1);
    toggleDone(course.id, lesson.id);
  };

  return (
    <div className="page lesson-layout" style={style}>
      <ReadingBar />
      <Outline course={course} current={lesson.id} />
      <motion.article
        className="lesson"
        key={lesson.id}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <header className="lesson-header">
          <motion.span className="lesson-icon" initial={{ rotate: -20, scale: 0.6 }} animate={{ rotate: 0, scale: 1 }} transition={{ type: 'spring', stiffness: 220 }}>
            <Icon name={lesson.icon} size={28} />
          </motion.span>
          <div>
            <p className="eyebrow">
              <Link to={`/course/${course.id}`}>Step {course.step} · {course.title}</Link> · Lesson {idx + 1} of {course.lessons.length}
            </p>
            <h1>{lesson.title}</h1>
            <p className="lede">{lesson.summary}</p>
            <span className="lesson-time"><Clock size={15} /> {lesson.minutes} min read</span>
          </div>
        </header>

        <div className="prose">
          <Blocks blocks={lesson.blocks} />
        </div>

        <div className="complete-row">
          <button type="button" className={`btn btn-complete ${done ? 'is-done' : ''}`} onClick={complete}>
            {burst > 0 && done && <Confetti key={burst} />}
            {done ? <CircleCheckBig size={18} /> : <Check size={18} />}
            {done ? 'Completed' : 'Mark lesson complete'}
          </button>
        </div>

        <nav className="pager">
          {prev ? (
            <Link to={`/course/${course.id}/${prev.id}`} className="pager-link"><ArrowLeft size={18} /><span><small>Previous</small>{prev.title}</span></Link>
          ) : <span />}
          {next ? (
            <Link to={`/course/${course.id}/${next.id}`} className="pager-link right"><span><small>Next</small>{next.title}</span><ArrowRight size={18} /></Link>
          ) : (
            <Link to={`/course/${course.id}/quiz`} className="pager-link right"><span><small>Finish the step</small>Take the quiz</span><Trophy size={18} /></Link>
          )}
        </nav>
      </motion.article>
    </div>
  );
}
